import { useState, type FormEvent } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Modal } from '@/components/Modal';
import { Button } from '@/components/Button';
import { api } from '@/api/client';
import { toast } from '@/state/toast';

interface Props {
  projectId: string;
  remarkId: string;
  onClose: () => void;
}

export function ReRemarkModal({ projectId, remarkId, onClose }: Props) {
  const qc = useQueryClient();
  const [reason, setReason] = useState('');
  const [body, setBody] = useState('');
  const [severity, setSeverity] = useState<'Critical' | 'High' | 'Medium' | 'Low'>('High');

  const create = useMutation({
    mutationFn: () => api.post(`/projects/${projectId}/remarks/${remarkId}/re-remark`, {
      body: body.trim(),
      severity,
      escalation_reason: reason.trim(),
    }),
    onSuccess: () => {
      toast.success('Повторное замечание создано');
      qc.invalidateQueries({ queryKey: ['remarks', projectId] });
      onClose();
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : 'Не удалось'),
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!reason.trim() || !body.trim()) return;
    create.mutate();
  };

  const ready = reason.trim().length > 0 && body.trim().length > 0;

  return (
    <Modal
      title={`Повторное замечание ${remarkId.slice(0, 8)}`}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Отмена</Button>
          <Button variant="primary" disabled={!ready || create.isPending} onClick={(e) => submit(e as unknown as FormEvent)}>
            {create.isPending ? 'Создание…' : 'Создать повторное'}
          </Button>
        </>
      }
    >
      <p className="text-muted" style={{ fontSize: 13, lineHeight: 1.55 }}>
        Создаётся новое замечание со ссылкой на исходное. ГИП получит уведомление с эскалацией.
      </p>

      <form onSubmit={submit} className="flex flex-col mt-4" style={{ gap: 16 }}>
        <div>
          <label className="block text-xs font-semibold uppercase tracking-wider text-muted mb-1.5">
            Причина повторного замечания (обязательно)
          </label>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={2}
            placeholder="Например: ответ ГИП v1 не закрывает требование по СП 37.13330.2012 п.4.3"
            className="w-full bg-white text-ink rounded-btn outline-none"
            style={{
              padding: '10px 14px',
              fontSize: 13,
              fontFamily: 'Inter, system-ui, sans-serif',
              border: reason.trim() ? '1px solid #E5E7EB' : '1px solid rgba(239,68,68,.4)',
            }}
          />
          {!reason.trim() && <p style={{ marginTop: 4, fontSize: 11, color: '#EF4444' }}>Обязательное поле</p>}
        </div>

        <div>
          <label className="block text-xs font-semibold uppercase tracking-wider text-muted mb-1.5">Важность</label>
          <div className="inline-flex bg-surface rounded-pill" style={{ padding: 4, gap: 4 }}>
            {(['Critical', 'High', 'Medium', 'Low'] as const).map((s) => {
              const active = severity === s;
              const labels = { Critical: 'Критическое', High: 'Высокое', Medium: 'Среднее', Low: 'Низкое' };
              const dots = { Critical: '#EF4444', High: '#F59E0B', Medium: '#6B7280', Low: '#9CA3AF' };
              return (
                <button
                  key={s}
                  type="button"
                  onClick={() => setSeverity(s)}
                  className="rounded-pill flex items-center"
                  style={{
                    padding: '6px 14px', gap: 6,
                    background: active ? '#FFFFFF' : 'transparent',
                    boxShadow: active ? '0 1px 3px rgba(10,22,40,.08)' : 'none',
                    fontWeight: active ? 600 : 500, fontSize: 13,
                    color: active ? '#0A1628' : '#6B7280',
                    border: 'none', cursor: 'pointer',
                  }}
                >
                  <span style={{ width: 8, height: 8, borderRadius: '50%', background: dots[s] }} />
                  {labels[s]}
                </button>
              );
            })}
          </div>
        </div>

        <div>
          <label className="block text-xs font-semibold uppercase tracking-wider text-muted mb-1.5">
            Текст повторного замечания
          </label>
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={5}
            placeholder="Сформулируйте требование к ГИПу более конкретно"
            className="w-full bg-white text-ink border border-border rounded-btn outline-none focus:border-ink"
            style={{ padding: '10px 14px', fontSize: 13, fontFamily: 'Inter, system-ui, sans-serif' }}
          />
        </div>
      </form>
    </Modal>
  );
}
